// P45c — Page /forgot-password : demande de réinitialisation
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link } from "react-router-dom";
import { Wrench, ArrowLeft, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useT } from "@/lib/i18n";
import { API_BASE } from "@/lib/apiBase";
const forgotSchema = z.object({
  email: z.string().email("Email invalide").min(1, "Email requis"),
});

type ForgotInput = z.infer<typeof forgotSchema>;

export default function ForgotPasswordPage() {
  const t = useT();
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotInput>({
    resolver: zodResolver(forgotSchema),
    defaultValues: { email: "" },
  });

  const onSubmit = async (data: ForgotInput) => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/v1/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: data.email }),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.detail || `API ${res.status}`);
      }
      setSubmitted(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <Link
            to="/login"
            className="mb-2 flex items-center gap-1 text-sm text-muted-foreground hover:text-primary"
          >
            <ArrowLeft className="h-4 w-4" />
            {t("forgot_password.back_to_login")}
          </Link>
          <CardTitle className="flex items-center gap-2">
            <Wrench className="h-5 w-5" />
            {t("forgot_password.title")}
          </CardTitle>
          <CardDescription>{t("forgot_password.subtitle")}</CardDescription>
        </CardHeader>
        <CardContent>
          {submitted ? (
            <div className="space-y-4">
              <div className="rounded-md border border-green-500 bg-green-50 p-4 text-sm text-green-900">
                <div className="flex items-center gap-2 font-semibold">
                  <Mail className="h-4 w-4" />
                  {t("forgot_password.email_sent_title")}
                </div>
                <p className="mt-2 text-xs">{t("forgot_password.email_sent_desc")}</p>
              </div>
              <Link
                to="/login"
                className="block text-center text-sm text-primary hover:underline"
              >
                {t("forgot_password.back_to_login")}
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
              <div className="space-y-1">
                <label htmlFor="email" className="text-sm font-medium">
                  {t("login.email")}
                </label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  data-testid="forgot-email-input"
                  {...register("email")}
                />
                {errors.email && (
                  <p className="text-xs text-destructive">{errors.email.message}</p>
                )}
              </div>

              {error && (
                <div className="rounded-md border border-destructive bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full"
                data-testid="forgot-submit-button"
              >
                {isLoading
                  ? t("forgot_password.submitting")
                  : t("forgot_password.submit")}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
